import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Link } from "wouter";
import { ArrowLeft, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUBSPECIALTIES = [
  {
    title: "Vasculitis",
    summary: "Vasculitis is one of the most complex areas in rheumatology, encompassing ANCA-associated vasculitis (GPA, MPA, EGPA), large vessel vasculitis (GCA, Takayasu), and rare entities such as polyarteritis nodosa and Behçet's disease.",
    training: [
      "Develop expertise in ANCA testing interpretation, BVAS scoring, and cross-sectional imaging (PET-CT, MRI)",
      "Gain experience in plasma exchange, cyclophosphamide protocols, and rituximab induction",
      "The UK Vasculitis network (including centres at Addenbrooke's, Hammersmith, and Salford) offers training opportunities",
      "Consider the BSR Biologics Register and EUVAS as research platforms",
      "The ANCA Vasculitis European Research (ANCER) network supports trainee involvement in multicentre trials",
    ],
    links: [
      { label: "EUVAS (European Vasculitis Study Group)", url: "https://www.vasculitis.org" },
      { label: "UK Vasculitis network", url: "https://www.bsrbr.org" },
    ],
  },
  {
    title: "Connective Tissue Diseases (CTD)",
    summary: "CTD encompasses SLE, systemic sclerosis (SSc), inflammatory myopathies (dermatomyositis, polymyositis, anti-synthetase syndrome), Sjögren's disease, mixed connective tissue disease (MCTD), and overlap syndromes.",
    training: [
      "Nailfold capillaroscopy and specialist investigations: myositis panel, ENA, complement, anti-dsDNA",
      "HRCT interpretation for ILD; pulmonary hypertension screening protocols in SSc",
      "Pregnancy in CTD — hydroxychloroquine safety, anti-Ro counselling, neonatal lupus",
      "Seek attachment to dedicated CTD clinics (lupus, SSc, myositis) at tertiary centres",
      "Join the BSR Rheumatology Research Group and EUSTAR (SSc registry)",
    ],
    links: [
      { label: "EUSTAR (SSc Registry)", url: "https://www.eustar.org" },
      { label: "UK JSLE Study Group", url: "https://www.uklupus.co.uk" },
    ],
  },
  {
    title: "Early Inflammatory Arthritis (EIA)",
    summary: "Early Inflammatory Arthritis clinics are a core part of modern rheumatology services, focused on rapid diagnosis of RA, PsA, and undifferentiated inflammatory arthritis to enable treat-to-target strategies from the earliest stage.",
    training: [
      "Musculoskeletal ultrasound (MSUS) — power Doppler, synovitis scoring, joint injection guidance",
      "Treat-to-target protocols and NICE TA criteria for bDMARD initiation",
      "Patient education, self-management, and early education programmes (LEAP equivalent)",
      "NICE QS33 for early RA and the EULAR/ACR 2022 classification criteria for RA",
      "Consider musculoskeletal ultrasound (MSUS) training courses with BSR and EULAR",
    ],
    links: [
      { label: "BSR MSUS Training", url: "https://www.rheumatology.org.uk/education-events/training/ultrasound" },
      { label: "NICE RA Guidance (NG100)", url: "https://www.nice.org.uk/guidance/ng100" },
    ],
  },
  {
    title: "Paediatric Rheumatology",
    summary: "Paediatric rheumatology covers JIA (all subtypes), childhood vasculitis, autoinflammatory diseases, lupus in adolescents, and transition to adult services. It requires dual adult and paediatric rheumatology competencies.",
    training: [
      "Dual competency pathway — apply through JRCPTB for paediatric training alongside rheumatology ST",
      "JIA subtypes: oligoarticular, RF-positive polyarticular, systemic (sJIA/MAS), enthesitis-related, psoriatic",
      "Uveitis monitoring protocols and ophthalmic liaison — JIA-associated uveitis is a major cause of morbidity",
      "Transition clinics: structured READY-Steady-Go or similar frameworks",
      "British Paediatric Rheumatology Group (BPRG) trainee network and annual meeting",
    ],
    links: [
      { label: "British Paediatric Rheumatology Group (BPRG)", url: "https://www.bspar.org.uk" },
      { label: "JRCPTB Paediatric Rheumatology Curriculum", url: "https://www.jrcptb.org.uk/specialties/paediatric-rheumatology" },
    ],
  },
  {
    title: "Academic & Research Rheumatology",
    summary: "Academic rheumatology encompasses clinical trials, translational research, epidemiology, and health services research. The UK has world-leading rheumatology research centres at Oxford, Cambridge, Manchester, Leeds, and UCL.",
    training: [
      "Apply for ACF (Academic Clinical Fellowship) at ST3 — 25% protected research time during clinical training",
      "ACL (Academic Clinical Lectureship) post-CCT — 50% research, typically requires a PhD/MD",
      "PhD/MD funding: MRC DTP, Wellcome Trust Clinical PhD, NIHR Academic Clinical Lectureship, BSR Foundation grants",
      "Consider the ARUK (Arthritis Research UK) trainee network and funding opportunities",
      "Develop GCP certification, protocol writing, and involvement in BSR Biologics Register or COUGAR/ORBIT trials",
    ],
    links: [
      { label: "NIHR Academic Clinical Fellowships", url: "https://www.nihr.ac.uk/explore-nihr/support/clinical-research-network.htm" },
      { label: "ARUK (Versus Arthritis Research)", url: "https://www.versusarthritis.org" },
    ],
  },
];

export default function Subspecialties() {
  return (
    <DoctorLayout title="Subspecialty Routes">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/doctor/career">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Career Pathways
            </Button>
          </Link>
        </div>

        <div className="bg-gradient-to-br from-violet-800 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <FlaskConical className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">Subspecialty Routes in Rheumatology</h2>
              <p className="text-violet-300 text-sm">Developing expertise beyond core rheumatology training</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            UK rheumatology training provides a broad foundation, but many consultants develop subspecialty interests during and after training. Planning your subspecialty exposure early — through attachments, electives, research, and conference engagement — is key to building expertise and a competitive portfolio.
          </p>
        </div>

        <div className="space-y-4">
          {SUBSPECIALTIES.map((sub) => (
            <div key={sub.title} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-900">{sub.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{sub.summary}</p>

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Training Tips</h4>
                <ul className="space-y-2">
                  {sub.training.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="text-violet-500 font-bold shrink-0 mt-0.5">·</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {sub.links.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {sub.links.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-3 py-1.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
                    >
                      {link.label} →
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DoctorLayout>
  );
}
